// src/data/immigrationGuides/skilledWorkerILR.js

const skilledWorkerILR = {
    slug: "skilled-worker-ilr",

    title: "Skilled Worker Visa ILR Guide UK: Settlement Requirements",

    seo: {
        metaTitle:
            "Skilled Worker Visa ILR Guide UK: Requirements After 5 Years",
        metaDescription:
            "Skilled Worker visa settlement requirements: qualifying period, continuous residence, salary rules, the Life in the UK Test and English requirements.",
        keywords: [
            "Skilled Worker ILR",
            "Skilled Worker visa settlement",
            "ILR after Skilled Worker visa",
            "UK work visa settlement"
        ]
    },

    breadcrumbs: [
        {
            name: "Home",
            url: "/"
        },
        {
            name: "Study Guide",
            url: "/study-guide"
        },
        {
            name: "Skilled Worker ILR",
            url: "/skilled-worker-ilr"
        }
    ],

    introduction: `
The [Skilled Worker visa](https://www.gov.uk/skilled-worker-visa) is one of the main employment routes that can lead to
Indefinite Leave to Remain (ILR) in the UK.

Eligible Skilled Worker visa holders may apply for settlement once they complete
the required qualifying period and meet all settlement requirements.

The exact requirements depend on the Immigration Rules in force at the time of
application.
`,

    sections: [
        {
            heading: "What is Skilled Worker ILR?",
            content: `
Skilled Worker ILR is settlement status obtained after meeting the requirements
of the Skilled Worker immigration route.

Successful applicants can remain in the UK without the time restrictions that
apply to temporary visas.

ILR can also be an important step before applying for British citizenship.
`
        },

        {
            heading: "How long does Skilled Worker ILR take?",
            content: `
Many Skilled Worker visa holders become eligible to apply for ILR after
completing the required qualifying period.

The qualifying period is normally based on continuous residence in eligible
immigration categories.

Applicants should check whether previous immigration permission can count
towards settlement.
`
        },

        {
            heading: "Skilled Worker ILR requirements",
            content: `
Applicants generally need to meet requirements including:

- qualifying residence period
- continuous residence requirements
- employment requirements
- salary requirements
- [English language requirements](https://www.gov.uk/english-language)
- Life in the UK Test requirement
- suitability requirements
`
        },

        {
            heading: "Continuous residence requirement",
            content: `
Skilled Worker ILR applicants must meet continuous residence requirements.

Applicants should review:

- time spent outside the UK
- travel history
- immigration permission periods

The permitted absences depend on the applicable Immigration Rules and the
period being assessed.
`
        },

        {
            heading: "Employment requirement",
            content: `
Applicants normally need to continue meeting the Skilled Worker route
requirements.

This may include:

- having qualifying employment
- meeting relevant job requirements
- meeting salary requirements

The Home Office assesses whether requirements are met at the time of
application.
`
        },

        {
            heading: "Salary requirement",
            content: `
Skilled Worker settlement applications may include salary requirements.

The salary rules depend on:

- occupation
- job code
- immigration rules in force
- applicant circumstances

Applicants should check the latest GOV.UK guidance before applying.
`
        },

        {
            heading: "Life in the UK Test",
            content: `
Most Skilled Worker ILR applicants need to pass the [Life in the UK Test](https://www.gov.uk/life-in-the-uk-test).

The test covers British:

- history
- government
- law
- traditions and customs
`
        },

        {
            heading: "English language requirement",
            content: `
Applicants normally need to meet the English language requirement unless they
qualify for an exemption.

Evidence may include accepted English qualifications or other approved methods.
`
        },

        {
            heading: "Application process",
            content: `
The Skilled Worker ILR application process normally involves:

1. Checking eligibility
2. Completing the settlement application
3. Paying the application fee
4. Uploading supporting documents
5. Providing biometrics where required
6. Waiting for a decision
`
        },

        {
            heading: "Common Skilled Worker ILR mistakes",
            content: `
Applicants should carefully check:

- whether their qualifying period is complete
- whether absences meet requirements
- whether salary requirements are satisfied
- whether supporting documents are complete
- whether Life in the UK and English requirements are met
`
        }
    ],

    checklist: [
        "Complete required qualifying period",
        "Check previous visa categories count if applicable",
        "Review UK absences",
        "Confirm current employment eligibility",
        "Check salary requirements",
        "Pass Life in the UK Test",
        "Meet English language requirement",
        "Prepare documents"
    ],

    faqs: [
        {
            question: "Can a Skilled Worker visa lead to ILR?",
            answer:
                "Yes. The Skilled Worker route is one of the UK immigration routes that may lead to settlement if eligibility requirements are met."
        },

        {
            question: "Do I need to stay with the same employer until ILR?",
            answer:
                "Applicants must meet the employment requirements that apply at the time of settlement application."
        },

        {
            question: "Do Skilled Worker dependants qualify for ILR?",
            answer:
                "Dependants may have their own settlement requirements and should check the rules that apply to their circumstances."
        },

        {
            question: "Is the Life in the UK Test required for Skilled Worker ILR?",
            answer:
                "Most applicants need to pass the Life in the UK Test unless exempt."
        },

        {
            question: "Can previous visas count towards Skilled Worker ILR?",
            answer:
                "Some previous immigration categories may count depending on the Immigration Rules and individual circumstances."
        }
    ],

    relatedPages: [
        {
            title: "ILR Guide",
            url: "/ilr-guide"
        },
        {
            title: "English Requirement",
            url: "/english-requirement"
        },
        {
            title: "Life in the UK Test",
            url: "/mock-exams"
        },
        {
            title: "UKVCAS Appointment Guide",
            url: "/ukvcas-appointment"
        }
    ],

    officialSources: [
        {
            title: "GOV.UK - Skilled Worker Visa Settlement",
            url: "https://www.gov.uk/indefinite-leave-to-remain-tier-2-t2-skilled-worker-visa"
        },
        {
            title: "GOV.UK - Immigration Rules",
            url: "https://www.gov.uk/guidance/immigration-rules"
        },
        {
            title: "GOV.UK - Life in the UK Test",
            url: "https://www.gov.uk/life-in-the-uk-test"
        }
    ],

    metadata: {
        contentType: "guide",
        category: "Immigration Guides",
        author: "Life in UK Coach",
        lastUpdated: "2026-07-20"
    }
};

export default skilledWorkerILR;